import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Users, Award, ListChecks, Lock, Loader2, ArrowLeft, MessageCircle } from 'lucide-react';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /community/profile/:handle — public community profile.
 *
 * Renders ONLY what the owner opted to share via their privacy
 * settings. Backend already enforces the visibility rules; this UI
 * just shows whatever the API returns.
 *
 * Privacy guarantee: NEVER displays dollar amounts or share counts.
 * Watchlist tickers come from the public_watchlists endpoint, not
 * verified_holdings.
 */
export default function CommunityProfile() {
  const { handle } = useParams();
  const [profile, setProfile] = useState(null);
  const [watchlists, setWatchlists] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await axios.get(`${API}/community/profile/${handle}`);
        if (cancelled) return;
        setProfile(data);
        // Pull the public watchlist list and filter by handle locally.
        const wlRes = await axios.get(`${API}/community/watchlists/public`, { params: { limit: 50 } });
        if (cancelled) return;
        const mine = (wlRes.data.watchlists || []).filter((w) => (w.handle || '').toLowerCase() === handle.toLowerCase());
        setWatchlists(mine);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || 'Member not found.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [handle]);

  if (loading) {
    return (
      <div className="min-h-screen min-h-[100svh] flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />Loading profile…
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen min-h-[100svh] bg-background" data-testid="community-profile-error">
        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-10 text-center">
          <Users className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <h1 className="text-xl font-bold">{error || 'Member not found.'}</h1>
          <Link to="/community" className="inline-block mt-4">
            <Button variant="outline"><ArrowLeft className="w-4 h-4 mr-1" /> Back to Community</Button>
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen min-h-[100svh] bg-background" data-testid="community-profile-page">
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-10">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
          <Link to="/community" className="hover:underline">Community</Link>
          <span>/</span>
          <span className="text-foreground/80">@{profile.handle}</span>
        </div>

        <header className="mb-6 pb-4 border-b">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-full bg-violet-100 dark:bg-violet-950/60 flex items-center justify-center shrink-0">
              <Users className="w-6 h-6 text-violet-600 dark:text-violet-400" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight">@{profile.handle}</h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                Joined {profile.joined_at ? new Date(profile.joined_at).toLocaleDateString() : '—'} ·{' '}
                {profile.public_watchlist_count || 0} public watchlist{profile.public_watchlist_count === 1 ? '' : 's'}
              </p>
            </div>
          </div>
        </header>

        <div className="space-y-5">
          {/* Health score */}
          {profile.health_score != null && (
            <section className="rounded-2xl border border-border bg-card p-4">
              <h2 className="text-base font-bold mb-2 flex items-center gap-2">
                <Lock className="w-4 h-4 text-violet-500 dark:text-violet-400" />
                Portfolio Quality
              </h2>
              <div className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{profile.health_score} / 100</div>
              <p className="text-xs text-muted-foreground">Average DiviDrip Quality Score across this member's holdings.</p>
            </section>
          )}

          {/* Badges */}
          {profile.badges && profile.badges.length > 0 && (
            <section className="rounded-2xl border border-border bg-card p-4">
              <h2 className="text-base font-bold mb-2 flex items-center gap-2">
                <Award className="w-4 h-4 text-violet-500 dark:text-violet-400" />
                Milestone Badges ({profile.badges.length})
              </h2>
              <div className="flex flex-wrap gap-2">
                {profile.badges.map((b) => (
                  <span
                    key={b.key}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-violet-100 dark:bg-violet-950/60 text-violet-800 dark:text-violet-300 text-xs"
                    title={b.blurb}
                  >
                    {b.emoji} {b.label}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Reply Rate — Round 80b. Soft engagement signal:
              what % of this member's comments got a reply within 24h?
              Hidden when the member has <3 comments (too noisy). */}
          {profile.reply_rate_24h_pct != null && (
            <section className="rounded-2xl border border-border bg-card p-4" data-testid="profile-reply-rate">
              <h2 className="text-base font-bold mb-2 flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-violet-500 dark:text-violet-400" />
                Conversation Starter
              </h2>
              <div className="flex items-baseline gap-3 flex-wrap">
                <div className="text-3xl font-bold text-violet-600 dark:text-violet-400">
                  {profile.reply_rate_24h_pct}%
                </div>
                <div className="text-xs text-muted-foreground">
                  reply rate · {profile.replies_received_24h || 0} of {profile.comments_made || 0} comments
                  got a reply within 24h
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">
                Higher means this member tends to spark conversation
                instead of just dropping one-liners.
              </p>
            </section>
          )}

          {/* Public watchlists */}
          <section className="rounded-2xl border border-border bg-card p-4">
            <h2 className="text-base font-bold mb-2 flex items-center gap-2">
              <ListChecks className="w-4 h-4 text-violet-500 dark:text-violet-400" />
              Public Watchlists
            </h2>
            {watchlists.length === 0 ? (
              <p className="text-sm text-muted-foreground">This member hasn't published any watchlists.</p>
            ) : (
              <ul className="space-y-2">
                {watchlists.map((w) => (
                  <li key={w.watchlist_id} className="rounded-lg border border-border bg-background p-2 text-sm">
                    <div className="font-semibold mb-1">{w.name}</div>
                    <div className="flex flex-wrap gap-1">
                      {(w.items || []).map((it) => (
                        <span
                          key={it.ticker}
                          className={`text-[11px] font-mono px-1.5 py-0.5 rounded ${
                            it.is_verified_owner
                              ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300'
                              : 'bg-muted'
                          }`}
                          title={it.is_verified_owner ? 'Verified Owner — imported via CSV within 90 days' : 'Not verified'}
                        >
                          {it.ticker}{it.is_verified_owner ? ' ✓' : ''}
                        </span>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <SiteDisclaimer />
      </main>
    </div>
  );
}
